'use client'

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react'
import { getTenantByPartnerIdAction } from '@/services/payload/payloadServerRepository'
import type { TenantLogoMediaData, TenantResultsData } from '@/types/tenant'
import { withAnalyticsTracking } from '@/utilities/analytics/apiAnalytics'
import { errorAnalyticsPayload } from '@/utilities/analytics/errorAnalytics'
import { trackEvent } from '@/utilities/analytics/trackEvent'

export type KitImageStatus = 'loading' | 'success' | 'error'

type KitImageContextValue = {
  kitImage: TenantLogoMediaData | null
  isLoading: boolean
  resolvedClientId: string | null
  status: KitImageStatus | null
  fetchKitImage: (clientId: string) => void
}

const KitImageContext = createContext<KitImageContextValue | null>(null)

function extractKitImage(tenant: TenantResultsData | null | undefined): TenantLogoMediaData | null {
  const kitImage = tenant?.kitIdImage
  if (kitImage && typeof kitImage === 'object') {
    return kitImage
  }
  return null
}

function preloadImageUrl(url: string, preloadedUrls: Set<string>) {
  if (typeof document === 'undefined' || preloadedUrls.has(url)) return

  preloadedUrls.add(url)
  const link = document.createElement('link')
  link.rel = 'preload'
  link.as = 'image'
  link.href = url
  document.head.appendChild(link)
}

function normalizeClientId(clientId: string): string | null {
  const normalizedClientId = clientId.trim()
  return normalizedClientId || null
}

function selectKitImageForClient(
  clientId: string | null | undefined,
  resolvedClientId: string | null,
  status: KitImageStatus | null,
  kitImage: TenantLogoMediaData | null,
  isLoading: boolean,
): { kitImage: TenantLogoMediaData | null; isLoading: boolean } {
  if (!clientId) {
    return { kitImage: null, isLoading: false }
  }

  const isResolvedForClient = resolvedClientId === clientId
  const isLoadingForClient =
    !isResolvedForClient || (isResolvedForClient && (status === 'loading' || isLoading))
  const kitImageForClient = isResolvedForClient && status === 'success' ? kitImage : null

  return {
    kitImage: kitImageForClient,
    isLoading: isLoadingForClient,
  }
}

export function KitImageProvider({ children }: Readonly<{ children: ReactNode }>) {
  const [kitImage, setKitImage] = useState<TenantLogoMediaData | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [resolvedClientId, setResolvedClientId] = useState<string | null>(null)
  const [status, setStatus] = useState<KitImageStatus | null>(null)
  const inFlightClientIdRef = useRef<string | null>(null)
  const resolvedClientIdRef = useRef<string | null>(null)
  const statusRef = useRef<KitImageStatus | null>(null)
  const preloadedUrlsRef = useRef(new Set<string>())

  const fetchKitImage = useCallback((clientId: string) => {
    const normalizedClientId = normalizeClientId(clientId)
    if (!normalizedClientId) return
    if (inFlightClientIdRef.current === normalizedClientId) return
    if (resolvedClientIdRef.current === normalizedClientId && statusRef.current === 'success') {
      return
    }

    inFlightClientIdRef.current = normalizedClientId
    resolvedClientIdRef.current = normalizedClientId
    statusRef.current = 'loading'
    setResolvedClientId(normalizedClientId)
    setStatus('loading')
    setKitImage(null)
    setIsLoading(true)

    void (async () => {
      try {
        const result = await withAnalyticsTracking(
          async () => {
            const response = await getTenantByPartnerIdAction(normalizedClientId)
            if (!response.success) {
              throw new Error(response.message || 'Unknown error')
            }
            return response
          },
          {
            eventName: 'client:get_tenant_by_partner_id_api',
            additionalProperties: { clientId: normalizedClientId },
          },
        )

        if (inFlightClientIdRef.current !== normalizedClientId) return

        const nextKitImage = result.data ? extractKitImage(result.data) : null
        statusRef.current = 'success'
        setStatus('success')
        setKitImage(nextKitImage)

        if (nextKitImage?.url) {
          preloadImageUrl(nextKitImage.url, preloadedUrlsRef.current)
        }
      } catch (error) {
        if (inFlightClientIdRef.current !== normalizedClientId) return
        trackEvent('client:failed to load tenant', {
          clientId: normalizedClientId,
          ...errorAnalyticsPayload(error),
        })
        statusRef.current = 'error'
        setStatus('error')
        setKitImage(null)
      } finally {
        if (inFlightClientIdRef.current === normalizedClientId) {
          inFlightClientIdRef.current = null
          setIsLoading(false)
        }
      }
    })()
  }, [])

  const value = useMemo(
    () => ({
      kitImage,
      isLoading,
      resolvedClientId,
      status,
      fetchKitImage,
    }),
    [fetchKitImage, isLoading, kitImage, resolvedClientId, status],
  )

  return <KitImageContext.Provider value={value}>{children}</KitImageContext.Provider>
}

function useKitImageContext() {
  const context = useContext(KitImageContext)
  if (!context) {
    throw new Error('useKitImage must be used within a KitImageProvider')
  }
  return context
}

export function useKitImagePrefetch(clientId: string | null | undefined, enabled: boolean): void {
  const { fetchKitImage } = useKitImageContext()

  useEffect(() => {
    // eslint-disable-next-line react-you-might-not-need-an-effect/no-event-handler -- event work runs in an effect; keep until refactor
    if (!enabled || !clientId) return
    fetchKitImage(clientId)
  }, [clientId, enabled, fetchKitImage])
}

export function useKitImage(clientId: string | null | undefined): {
  kitImage: TenantLogoMediaData | null
  isLoading: boolean
} {
  const { kitImage, isLoading, resolvedClientId, status } = useKitImageContext()
  return selectKitImageForClient(clientId, resolvedClientId, status, kitImage, isLoading)
}

export function useKitImageRegistrationEntry(clientId: string | null | undefined): {
  kitImage: TenantLogoMediaData | null
  isLoading: boolean
} {
  const { fetchKitImage, kitImage, isLoading, resolvedClientId, status } = useKitImageContext()

  useEffect(() => {
    // eslint-disable-next-line react-you-might-not-need-an-effect/no-event-handler -- event work runs in an effect; keep until refactor
    if (!clientId) return
    fetchKitImage(clientId)
  }, [clientId, fetchKitImage])

  return selectKitImageForClient(clientId, resolvedClientId, status, kitImage, isLoading)
}

export function useKitImageSafe() {
  return useContext(KitImageContext)
}
